import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { WorkOrderService } from '../../core/services/work-order.service';
import { CustomerService } from '../../core/services/customer.service';
import { TeamService } from '../../core/services/team.service';
import { OrganizationContextService } from '../../core/services/organization-context.service';
import {
  WorkOrder,
  WorkOrderStatus,
  workOrderAcresDisplay,
  workOrderLocationDisplay,
  workOrderStatusBadgeClass,
  workOrderStatusLabel,
  workOrderSourceLabel,
} from '../../core/models/work-order.model';
import { MembershipRole, RoleHelper } from '../../core/models/team.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * Work Orders list page — the org Owner / Chief Supervisor's queue
 * view. Renders a filterable table (status chips, assigned pilot,
 * customer, scheduled-date range, title search) over the work orders
 * loaded by `WorkOrderService`.
 *
 * Rows are clickable → `/work-orders/:id`. A `+ New Work Order` button
 * top-right launches the create form.
 */
@Component({
  selector: 'app-work-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective],
  templateUrl: './work-orders.component.html',
  styleUrls: ['./work-orders.component.scss'],
})
export class WorkOrdersComponent implements OnInit {
  private workOrderService = inject(WorkOrderService);
  private customerService = inject(CustomerService);
  private teamService = inject(TeamService);
  private organizationContext = inject(OrganizationContextService);
  private router = inject(Router);

  // ─── Filter state ──────────────────────────────────────────────────
  statusFilter = signal<WorkOrderStatus | 'all'>('all');
  assignedFilter = signal<string>('all');
  customerFilter = signal<string>('all');
  dateFrom = signal<string>('');
  dateTo = signal<string>('');
  searchQuery = signal<string>('');

  // ─── Computed lists ────────────────────────────────────────────────

  readonly workOrders = computed(() => this.workOrderService.workOrders());

  readonly customers = computed(() =>
    this.customerService.customers().map((c) => ({ id: c.id, name: c.name })),
  );

  /**
   * Org members who can be assigned to a WO. Per the spec: Pilot,
   * PilotInCharge, or Owner. Chief Sups can show up too — the spec is
   * loose here so we widen to "anyone in the org" once we have the
   * full list and let the picker do the talking.
   */
  readonly assignablePilots = computed(() => {
    return this.teamService
      .members()
      .filter((m) => !m.deletedAt)
      .filter((m) => {
        const role = m.combinedRole ?? m.role ?? 0;
        return (
          RoleHelper.hasRole(role, MembershipRole.Pilot) ||
          RoleHelper.hasRole(role, MembershipRole.PilotInCharge) ||
          RoleHelper.hasRole(role, MembershipRole.Owner)
        );
      })
      .map((m) => ({
        id: m.profileId,
        name: this.memberDisplayName(m.firstName, m.lastName, m.email),
      }));
  });

  readonly filtered = computed(() => {
    const status = this.statusFilter();
    const assigned = this.assignedFilter();
    const customer = this.customerFilter();
    const from = this.dateFrom();
    const to = this.dateTo();
    const q = this.searchQuery().trim().toLowerCase();

    return this.workOrders().filter((wo) => {
      if (status !== 'all' && wo.status !== status) return false;
      if (assigned !== 'all') {
        if (assigned === 'unassigned') {
          if (wo.assignedPilotProfileId) return false;
        } else if (wo.assignedPilotProfileId !== assigned) {
          return false;
        }
      }
      if (customer !== 'all' && wo.customerId !== customer) return false;
      if (from && (!wo.scheduledDate || wo.scheduledDate < from)) return false;
      if (to && (!wo.scheduledDate || wo.scheduledDate > to)) return false;
      if (q) {
        const haystack = [
          wo.title,
          wo.description ?? '',
          wo.fieldName ?? '',
          wo.customerName ?? '',
          wo.assignedPilotName ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  });

  // Per-status counts on the unfiltered list so chip badges stay stable
  // as the user clicks between them.
  readonly statusCounts = computed(() => {
    const counts: Record<WorkOrderStatus | 'all', number> = {
      all: 0,
      open: 0,
      in_progress: 0,
      completed: 0,
      cancelled: 0,
    };
    for (const wo of this.workOrders()) {
      counts.all++;
      counts[wo.status]++;
    }
    return counts;
  });

  ngOnInit(): void {
    void this.workOrderService.loadAll();
    // Customers + team load via their own services on org-context change,
    // but if this page is the cold landing, prod a refresh.
    const orgId = this.organizationContext.getCurrentOrganizationId();
    if (orgId) {
      void this.teamService.loadMembers(orgId);
    }
  }

  // ─── Actions ──────────────────────────────────────────────────────

  newWorkOrder(): void {
    this.router.navigate(['/work-orders/new']);
  }

  view(wo: WorkOrder): void {
    this.router.navigate(['/work-orders', wo.id]);
  }

  clearFilters(): void {
    this.statusFilter.set('all');
    this.assignedFilter.set('all');
    this.customerFilter.set('all');
    this.dateFrom.set('');
    this.dateTo.set('');
    this.searchQuery.set('');
  }

  // ─── View helpers ─────────────────────────────────────────────────

  readonly statusOptions: { value: WorkOrderStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'open', label: 'Scheduled' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Complete' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  statusLabel = workOrderStatusLabel;
  statusBadgeClass = workOrderStatusBadgeClass;
  sourceLabel = workOrderSourceLabel;
  locationDisplay = workOrderLocationDisplay;
  acresDisplay = workOrderAcresDisplay;

  /**
   * Tile of the assigned pilot's name from whatever the WO payload
   * carries, falling back to the team-service lookup. The Hub may or
   * may not denormalize `assignedPilotName` onto the list response;
   * the local lookup keeps us honest.
   */
  pilotName(wo: WorkOrder): string {
    if (wo.assignedPilotName) return wo.assignedPilotName;
    if (!wo.assignedPilotProfileId) return 'Unassigned';
    const member = this.teamService
      .members()
      .find((m) => m.profileId === wo.assignedPilotProfileId);
    if (!member) return 'Assigned';
    return this.memberDisplayName(member.firstName, member.lastName, member.email);
  }

  private memberDisplayName(
    firstName?: string,
    lastName?: string,
    email?: string,
  ): string {
    const name = [firstName, lastName].filter(Boolean).join(' ').trim();
    return name || email || 'Unknown';
  }
}
